import { ApolloClient, InMemoryCache } from "@apollo/client";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import { useTranslation } from "next-i18next";
import { ArrowLeft, Checkmark, Email, HandLogo, Twitter } from "../../assets";
import Recommendations from "../../components/ArticleRecommendation/ArticleRecommendation";
import DiscoverBeneBono from "../../components/DiscoverBeneBono/DiscoverBeneBono";
import Footer from "../../components/Footer/Footer";
import Navbar from "../../components/Nav/Nav";
import NavHook from "../../components/Nav/NavHook";
import ShareButton from "../../components/ShareButton/ShareButton";
import { GET_LAST_ARTICLES_BY_TAGS } from "../../graphql/posts";
import {
  GET_ALL_RECIPES,
  GET_ALL_SLUGS,
  GET_INDIVIDUAL_RECIPE,
  GET_LAST_RECIPES,
} from "../../graphql/recipes";
import { timeConvert } from "../../utils/helpers";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import RefreshedFooter from "../../components/RefreshedFooter/RefreshedFooter";
import RecipeSeasonTag from "../../components/RecipeSeasonTag/RecipeSeasonTag";
import RecipeDifficultyTag from "../../components/RecipeDifficultyTag/RecipeDifficultyTag";
import RecipeRegimeTag from "../../components/RecipeRegimeTag/RecipeRegimeTag";

const client = new ApolloClient({
  uri: process.env.STRAPI_GRAPHQL_URL,
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: "no-cache",
    },
  },
});

export default function RecipePage({
  recipe,
  latestPosts,
  footer,
  navigation,
}) {
  const router = useRouter();
  const { t } = useTranslation();

  useEffect(() => {
    window?.rudderanalytics?.page();
  }, []);
  return (
    <>
      <Head>
        <title>{recipe?.title}</title>
        <meta
          property="og:title"
          content={recipe?.seo?.metaTitle}
        />
        <meta
          property="og:site_name"
          content="Bene Bono"
        />
        <meta
          property="og:description"
          content={recipe?.seo?.metaDescription}
        />
        <meta
          property="og:type"
          content="website"
        />
        <meta
          property="og:image"
          content={recipe?.seo?.metaImage?.data?.attributes?.url}
        />
        {process.env.NODE_ENV !== "production" && (
          <meta
            name="robots"
            content="index,follow"
          />
        )}
      </Head>
      <Navbar links={navigation.links} />
      <div className="single_recipe">
        <div className="article_recipe_header">
          <span
            className="navigation_button"
            onClick={() => router.push("/recipes")}
          >
            <ArrowLeft /> {t("back.to.recipes")}
          </span>
          <h1>{recipe?.title}</h1>

          <div className="recipes_duration">
            <p>
              {recipe?.duration <= 60
                ? `${recipe?.duration} min`
                : timeConvert(recipe?.duration)}
            </p>
          </div>


          <div className="difficulty_and_diet_tag_single_recipe">
            <RecipeDifficultyTag difficulty={recipe.recipe_difficulty.data[0]?.attributes.name} />
            <ul className="diet_tags">
              {recipe.dietTag.data.map((tag, i) => (
                <RecipeRegimeTag key={i} regime={tag?.attributes.name} />
              ))}
            </ul>
          </div>

          <ul className="tags">
            {recipe?.season_tags?.data.map((t, i) => (
              <RecipeSeasonTag key={i} season={t?.attributes?.name} isSinglePost={true} />
            ))}
          </ul>






          {/* <div className="recipe_info">
            <p>{recipe?.recipe_difficulty?.data[0]?.attributes?.name}</p>
            <p>
              {recipe?.duration <= 60
                ? `${recipe?.duration} min`
                : timeConvert(recipe?.duration)}
            </p>
          </div> */}
        </div>
        <div className="article_content_recipe">
          <div className="ingredients_container">
            <div className="ingredients_content">
              <img src={recipe?.preview_image?.data[0]?.attributes?.url} />
              <h2>
                {t("ingredients.title")}
                {recipe?.ingredientBaseUnit && recipe?.ingredientBaseUnitName
                  ? ` ${t("ingredients.title.for")} ${recipe?.ingredientBaseUnit
                  } ${recipe?.ingredientBaseUnitName}`
                  : ""}
              </h2>
              <ul className="ingredients">
                {recipe?.ingredient?.map((i) => (
                  <li key={i}>
                    <Checkmark />
                    <p>
                      {i?.ingredient.charAt(0).toUpperCase() +
                        i?.ingredient.slice(1)}
                    </p>
                    <span>
                      {i?.quantity} {i?.unit}
                    </span>
                  </li>
                ))}
              </ul>
              <h2>{t("preparation.title")}</h2>
              <ul className="steps">
                {recipe?.step?.map((s, i) => (
                  <div key={i} className="step">
                    <div className="steps_header">
                      <HandLogo />
                      <h5>
                        {t("recipes.step")} {i + 1}
                      </h5>
                    </div>
                    <li>{s?.step}</li>
                  </div>
                ))}
              </ul>
              <p className="text_after_step">Bon appétit !</p>
            </div>

          </div>
        </div>
          <DiscoverBeneBono posts={latestPosts} isRecipe={true} />
      </div>
      {/* <Recommendations
        link="recipes"
        title={t("last.recipes.title")}
        data={latestPosts}
      /> */}
      <RefreshedFooter links={footer.links} />
    </>
  );
}

export async function getStaticPaths({ locale }) {
  const recipes = await client.query({
    query: GET_ALL_RECIPES,
    variables: {
      page: 1,
      pageSize: 12,
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const { data } = await client.query({
    query: GET_ALL_SLUGS,
    variables: {
      pageSize: parseInt(recipes.data.recettes.meta.pagination.total),
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const paths = data.recettes.data.map((recipe) => {
    return {
      params: { slug: recipe.attributes.slug },
    };
  });

  return {
    paths,
    fallback: "blocking",
  };
}

export async function getStaticProps({ params, locale }) {
  const { data } = await client.query({
    query: GET_INDIVIDUAL_RECIPE,
    variables: {
      slugUrl: params.slug,
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const latestPosts = await client.query({
    query: GET_LAST_RECIPES,
    variables: {
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const mainNav = await NavHook(locale, "main");
  const navigation = {
    links: mainNav,
  };

  const footerNav = await NavHook(locale, "footer");
  const footer = {
    links: footerNav,
  };

  if (!data || !latestPosts) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(locale)),
      recipe: data.recettes.data[0].attributes,
      latestPosts: latestPosts.data.recettes.data,
      navigation,
      footer,
      locale,
    },
    revalidate: 15,
  };
}
